//import JsStore from '../assets/vendor/js/jsstore.min.js'; // It has no module to export

"use strict"; // Required by Terser minifier

/**------------------------------------
DB Schema
Name 	ShadesMasterDB

Tables
- WorkingImages - it stores the images the user is playing with.
		idImageRemote - the image remote id (primary key).
		topic - the string that identifies the image category.
		svg - it contains the svg markup to be injected in the web page.
		shadowBase64 - it contains the shadow image in base64 format.
		metadata - still empty.
		done [0|1|2] - 0 image never opened, 1 game started, 2 game finished.
		suspendedSvgMarkup - the svg of a partially completed image.
		urlThumb - the url for a thumbnails from unsplash.com.
		urlImage - the url for the real image from unsplash.com.

- CurrentWorkingImage - it stores the id of the current image. It contains 1 record at maximum.
		idImageRemote - identify the image currently selected.

- DoneImages - it stores every finished image.
		idImageRemote - the image remote id (primary key).
		topic - the string that identifies the image category.
		urlThumb - the url for a thumbnails from unsplash.com.
		urlImage - the url for the real image from unsplash.com.
		date - the date when the user completed the image. It's in mmss.

- Options - it stores the game options
		idOption - number. It's always 1.
		difficulty - [1|2|3] easy, medium, hard.
		outline - [true|false] if true shows the image shadow.
		autoBrush - [true|false] if true the next brush will be automatically selected.
		sound - [true|false] if true the sound effects will play when needed.
-------------------------------------*/
var jsDatabase = {
	name: 'ShadesMasterDB',
	//version: 5, // Increasing the DB version allow to modify the schema structure
	version: 6, // The version 6 abandon the table WorkingImages for a new table WorkinImages_v6
	tables: [
		// It's easier leave behind the table than altering its types.
		// {
		// 	name: 'WorkingImages',
		// 	columns: {
		// 		idImageRemote: { primaryKey: true, notNull: true, dataType: "string" },
		// 		topic: { notNull: true, dataType: "string" },
		// 		svg: { notNull: true, dataType: "string" },
		// 		metadata: { notNull: false, dataType: "string" },
		// 		done: { notNull: true, dataType: "number" },
		// 		suspendedSvgMarkup: { dataType: "string" },
		// 		urlThumb: { dataType: "string" },
		// 		urlImage: { dataType: "string" },
		// 	},
		// },
		{
			name: 'WorkingImages_v6',
			columns: {
				idImageRemote: { primaryKey: true, notNull: true, dataType: "string" },
				topic: { notNull: true, dataType: "string" },
				svg: { notNull: true, dataType: "object" },
				shadowBase64: { dataType: 'object' },
				metadata: { notNull: false, dataType: "string" },
				done: { notNull: true, dataType: "number" },
				suspendedSvgMarkup: { dataType: "object" },
				urlThumb: { dataType: "string" },
				urlImage: { dataType: "string" },
			},
		},
		{
			name: 'CurrentWorkingImage',
			columns: {
				idImageRemote: { primaryKey: true, dataType: "string" },
			},
		},
		{
			name: 'DoneImages',
			columns: {
				idImageRemote: { primaryKey: true, dataType: "string" },
				topic: { notNull: true, dataType: "string" },
				urlThumb: { dataType: "string" },
				urlImage: { dataType: "string" },
				date: { dataType: "number" }, // New column for DB version 5
			},
			alter: {
				5: { // With DB version 5 add the column date and update every record with Date.now()
					add: {
						date: {
							dataType: 'number',
						}
					}
				}
			}
		},
		{
			name: 'Options',
			columns: {
				idOption: { primaryKey: true, dataType: "number" },
				difficulty: { dataType: "string" }, // [1|2|3] easy, medium, hard.
				outline: { dataType: "boolean" }, // [true|false] if true shows the image shadow.
				autoBrush: { dataType: "boolean" }, // [true|false] if true the next brush will be automatically selected.
				sound: { dataType: "boolean" }, // [true|false] if true the sound effects will play when needed.
			},
		},
	]
};

/**------------------------------------
Initialize the database.
-------------------------------------*/
var initDB = async function () {

	const conn = new JsStore.Connection(new Worker("./assets/vendor/js/jsstore.worker.min.js"));
	
	// For debugging
	//conn.logStatus = true;

	const isDbCreated = await initializeDatabase(conn);

	if (isDbCreated === true) {
		console.log("db created");
		if (conn.database.version == 5) {
			// Update the new field for db version 5
			console.log("db updating to version 5");
			await conn.update({
				in: "DoneImages",
				set: {
					date: Date.now(),
				}
			});
		}
	} else {
		console.log("db opened");
	}

	await closeConnection(conn);
}

/**------------------------------------
Helper to open the db connection.
-------------------------------------*/
var openConnection = async function () {
	const conn = new JsStore.Connection(new Worker("./assets/vendor/js/jsstore.worker.min.js"));
	await initializeDatabase(conn);
	return conn;
}

var initializeDatabase = async function (conn) {
	const isDbCreated = await conn.initDb(jsDatabase);
	return isDbCreated;
}

/**------------------------------------
Helper to close the db connection.
@param {Object} conn - the opened connection.
-------------------------------------*/
var closeConnection = async function (conn) {
	await conn.terminate();
}

// Initialize the database.
initDB();

const db = {}

//---------------------------------------------------
// WorkingImages
//---------------------------------------------------

/**------------------------------------
Add to the DB a new image in processing downloaded from the server.
@param {Object} data - It contains the data received from the server.
@param {Object} success - Callback.
@param {Object} failure - Callback.
-------------------------------------*/
db.writeImageFromRemote = async function (data, success, failure) {

	var conn = await openConnection();

	try {
		const newImage = await conn.insert({
			into: 'WorkingImages_v6',
			values: [
				{
					topic: data.topic,
					idImageRemote: data.idImage,
					// Convert the big fields SVG and ShadowImage in js objects to store them as blob. This way the IndexedDB manifest file doesn't grow
					svg: { body: data.SVG },
					shadowBase64: { body: data.ShadowImage },
					metadata: "",
					done: 0, // [0 - downloaded, 1 - started, 2 - finished]
				},
			],
			return: true,
		});
		//console.log(`new id image ${newImage[0].idImage}`);
		await writeCurrentImageInternal(conn, newImage[0].idImageRemote, success);
	}
	catch {
		failure();
	}
	await closeConnection(conn);
};

/**------------------------------------
Return all topics that have an image in processing.
@param {Object} success - Callback.
-------------------------------------*/
db.getAllProcessingImages = async function (success) {

	var conn = await openConnection();

	// Find any unfinished item available for a certain topic.
	var arr = await conn.select({
		from: 'WorkingImages_v6'
	});

	var arr2 = [];

	if (arr.length > 0) {
		arr2 = arr.map(i => (i.topic));
	}
	await closeConnection(conn);
	success(arr2);
	return arr2;
};

/**------------------------------------
According to the topic requested, it returns the id of an uncompleted image if available.
@param {Object} topic - The image category.
@param {Object} success - Callback
@param {Object} failure - Callback
-------------------------------------*/
db.readImageFromLocal = async function (topic, success, failure) {

	var conn = await openConnection();

	// Find any unfinished item already available for a certain topic
	var arr = await conn.select({
		from: 'WorkingImages_v6',
		where: {
			done: { in: [0, 1] },
			topic: topic,
		},
	});

	if (arr.length > 0) {
		await writeCurrentImageInternal(conn, arr[0].idImageRemote, success);
	} else {
		failure();
	}
	await closeConnection(conn);
};

/**------------------------------------
Insert in CurrentWorkingImage table the new current image id.
@param {Object} conn - opened db connection.
@param {Number} idImageRemote - the id of the new current image.
@param {Object} callback - the callback to call at the end.
-------------------------------------*/
var writeCurrentImageInternal = async function (conn, idImageRemote, callback) {
	// Clear any previous current image and insert the new current one
	await conn.clear('CurrentWorkingImage');
	await conn.insert({
		into: 'CurrentWorkingImage',
		//upsert: true,
		values: [{ idImageRemote: idImageRemote }],
	});

	if (callback) {
		callback();
	}
}

/**------------------------------------
Get the current image.
@param {Object} ready - The callback to be called when the image has been retrieved
-------------------------------------*/
db.readCurrentImage = async function (ready) {

	var conn = await openConnection();

	const currImageId =
		await conn.select({
			from: 'CurrentWorkingImage',
		});
	
	if (currImageId.length == 0)
	{
		await closeConnection(conn);

		// No current image. Error? 
		ready(undefined);

		return;
	}

	// If a current image id has been found, the WorkingImages table can be query to extract the full image data.
	const currImage =
		await conn.select({
			from: 'WorkingImages_v6',
			where: {
				idImageRemote: currImageId[0].idImageRemote,
			},
		});
	
	var img = currImage[0];

	// Extract the big fields stored as js objects to return simple strings.
	img.svg = img.svg.body;
	img.shadowBase64 = img.shadowBase64.body;
	img.suspendedSvgMarkup = img.suspendedSvgMarkup ? img.suspendedSvgMarkup.body : null;

	ready(img);

	await closeConnection(conn);
};

/**------------------------------------
Delete an uncompleted image.
@param {String} idImageRemote - The unsplash image id.
@param {Object} success - The callback to be called when the image has been deleted.
-------------------------------------*/
db.deleteUncompletedImage = async function (idImageRemote, success) {
	
	var conn = await openConnection();

	await conn.remove({
		from: 'WorkingImages_v6',
		where: {
			idImageRemote: idImageRemote,
		},
	});
	
	// Clear tracks from the CurrentImage table too.
	await conn.clear('CurrentWorkingImage');

	await closeConnection(conn);

	if (success) {
		success();
	}
};

/**------------------------------------
Delete all downloaded items (only for debugging)
-------------------------------------*/
db.emptyDB = async function () {

	var conn = await openConnection();

	await conn.clear('WorkingImages_v6');
	await conn.clear('CurrentWorkingImage');
	await conn.clear('DoneImages');
	//await conn.clear('Options');

	await closeConnection(conn);
};

/**------------------------------------
Change the done level to 1 for the image and set the svg markup with the partially completed version.
@param {Number} idImageRemote - The remote image id.
@param {String} currentSvg - The partially completed SVG.
-------------------------------------*/
db.setSuspendImage = async function (idImageRemote, currentSvg) {

	var conn = await openConnection();

	await conn.update({
		in: 'WorkingImages_v6',
		where: {
			idImageRemote: idImageRemote
		},
		set: {
			done: 1,
			// Convert the big field currentSvg in a js object to store it as blob.
			suspendedSvgMarkup: { body: currentSvg },
		},
	});

	await closeConnection(conn);
}

//---------------------------------------------------
// DoneImages
//---------------------------------------------------

/**------------------------------------
Get a string array with all the done images for a certain topic.
Async function.
@param {Object} conn - opened db connection.
@param {String} topicFilter - The image category. If topic is not supplied the where filter is ignored.
-------------------------------------*/
var getDoneImageListInternal = async function (conn, topicFilter) {
	var whereClause = { topic: topicFilter }; // Extract only the images for a certain topic.

	if (!topicFilter) {
		// topicFilter not supplied.
		whereClause = { topic: { like: '%' } }; // Extract everything.
	}

	const doneImageList =
		await conn.select({
			from: "DoneImages",
			where: whereClause,
			order: {
				by: "date",
				type: "desc" //supprted sort type is - asc,desc
			}
		});
	return doneImageList;
}

/**------------------------------------
Get a string array with all the done images for a certain topic.
Async function.
@param {String} topicFilter - The image category. If topic is not supplied the where filter is ignored.
-------------------------------------*/
db.getDoneImageList = async function (topicFilter) {
	
	var conn = await openConnection();

	var doneImageList = await getDoneImageListInternal(conn, topicFilter);

	await closeConnection(conn);

	return doneImageList;
}

/**------------------------------------
Get an array with all the remote ids for done images for a certain topic.
@param {String} topic - The image category.
-------------------------------------*/
db.getRemoteIdListOfDoneImages = async function (topic) {
	
	var conn = await openConnection();

	var arrImage = await getDoneImageListInternal(conn, topic);

	await closeConnection(conn);

	return arrImage.map(a => a.idImageRemote);
}

/**------------------------------------
Add a finished image.
Async function.
@param {String} topic - The image category.
@param {String} idImageRemote - The unsplash image id.
@param {String} urlThumb - The thumbnail url.
@param {String} urlImage - The original image page url.
@param {Number} date - The date of completion.
-------------------------------------*/
db.addDoneImage = async function (topic, idImageRemote, urlThumb, urlImage, date) {
	
	var conn = await openConnection();

	if (!date)
	{
		date = Date.now();
	}

	try {
		await conn.insert({
			into: "DoneImages",
			values: [
				{
					topic: topic,
					idImageRemote: idImageRemote,
					urlThumb: urlThumb,
					urlImage: urlImage,
					date: date
				},
			],
			return: false,
		});
	}
	catch {
		console.log(`jsstoreHelper.addDoneImage failed for topic ${topic}`);
	}

	await closeConnection(conn);
}

/**------------------------------------
Remove an image from the group of the finished ones.
Async function.
@param {String} idImageRemote - The unsplash image id.
-------------------------------------*/
db.deleteDoneImage = async function (idImageRemote) {

	var conn = await openConnection();

	await db.remove({
		from: "DoneImages",
		where: {
			idImageRemote: idImageRemote,
		},
	});

	await closeConnection(conn);
};

//---------------------------------------------------
// Options
//---------------------------------------------------

/**------------------------------------
Get the options for the game.
Async function.
@param {Object} conn - opened db connection.
Return object structure:
	difficulty: 1|2|3
	outline: true|false
	sound: true|false
	autoBrush: true|false
	percentageToFillAColor: number
	percentageToWin: number
-------------------------------------*/
var getOptionsInternal = async function (conn) {

	var options =
		await conn.select({
			from: "Options",
		});
	
	var optObj = {};

	if (options.length > 0) {
		optObj = options[0];
	}
	
	if (typeof (optObj.difficulty) == 'undefined') {
		optObj.difficulty = '2';
	}

	if (typeof (optObj.outline) == 'undefined') {
		optObj.outline = true;
	}

	if (typeof (optObj.sound) == 'undefined') {
		optObj.sound = true;
	}

	if (typeof (optObj.autoBrush) == 'undefined') {
		optObj.autoBrush = false;
	}

	switch (optObj.difficulty) {
		case '1':
			optObj.percentageToFillAColor = 70;
			optObj.percentageToWin = 75;
			break;
		case '2':
		default:
			optObj.percentageToFillAColor = 75;
			optObj.percentageToWin = 80;
			break;
		case '3':
			optObj.percentageToFillAColor = 90;
			optObj.percentageToWin = 99;
			break;
	}

	return optObj;
}

/**------------------------------------
Get the options for the game.
Async function.
@param {Object} conn - opened db connection.
Return object structure:
	difficulty: 1|2|3
	outline: true|false
	sound: true|false
	autoBrush: true|false
	percentageToFillAColor: number
	percentageToWin: number
-------------------------------------*/
db.getOptions = async function () {
	var conn = await openConnection();
	var optObj = await getOptionsInternal(conn);
	await closeConnection(conn);
	return optObj;
}

/**------------------------------------
Save the options for the game.
Return the new options to the caller.
Async function with callback.
@param {Object} instance of options.
@param {Function} success - The callback.
-------------------------------------*/
db.saveOptions = async function (options, success) {
	
	var conn = await openConnection();

	await conn.insert({
		into: 'Options',
		upsert: true, // If it already exists it update the existing record
		values: [
			{
				idOption: 1,
				difficulty: options.difficulty,
				outline: options.outline,
				sound: options.sound,
				autoBrush: options.autoBrush
			}
		]
	})

	var retOptions = await getOptionsInternal(conn);

	await closeConnection(conn);

	if (success) {
		success(retOptions);
	}

	return retOptions;
};

export default db;